import * as React from "react";
import {
  useState,
  useEffect,
  useRef,
  type ChangeEvent,
  type ComponentProps,
  type MouseEvent,
} from "react";
import {
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  Box,
  Dialog,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  EmailOutlined as EmailOutlinedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getMails } from "../../../api";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../components/SnackBarCustom";
import { formatDate } from "../../../utils/deteUtils";
import type {
  MailSearchContents,
  SendMailItem,
  SendMailOrganization,
} from "../../../types/mail";

type MailLogColumn = {
  id: keyof SendMailItem | "sent_status" | "send_mail_organizations";
  label: string;
  minWidth: string;
  align?: "left" | "right";
  isSmallScreen: boolean;
  organizations?: boolean;
  disaster?: boolean;
  button?: boolean;
};

type SearchResultProps = {
  searchContents: MailSearchContents;
};

const columns: MailLogColumn[] = [
  {
    id: "plan_at",
    label: "送信予定日時",
    minWidth: "fit-content",
    isSmallScreen: true,
  },
  {
    id: "sent_at",
    label: "送信日時",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "send_mail_organizations",
    label: "連絡先",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: true,
    organizations: true,
  },
  {
    id: "title",
    label: "件名",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "disaster_id",
    label: "添付災害スレッド",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
    disaster: true,
  },
  {
    id: "sent_status",
    label: "",
    minWidth: "fit-content",
    align: "left",
    button: true,
    isSmallScreen: true,
  },
];

const SearchResult = ({ searchContents }: SearchResultProps) => {

  const navigate = useNavigate();
  const handleNavigate = (e: MouseEvent, path: string) => {
    e.stopPropagation();
    navigate(path);
  };

  //メール履歴一覧取得

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalData, setTotalData] = useState<number | undefined>();
  const [mails, setMails] = useState<SendMailItem[]>([]);

  const lastSearchedContentRef = useRef<MailSearchContents | null>(null);

  const fetchMails = async (overridePage?: number) => {
    const pageToUse = overridePage !== undefined ? overridePage : page;
    // 検索ボタン押下時は現在の検索条件、ページネーション時は最後に確定した条件を使用
    const contentToUse =
      overridePage !== undefined
        ? searchContents
        : (lastSearchedContentRef.current ?? searchContents);
    try {
      const response = await getMails({
        sent_at_stt: contentToUse.sent_at_stt ?? null,
        sent_at_end: contentToUse.sent_at_end ?? null,
        title: contentToUse.title ?? null,
        m_role_id: contentToUse.m_role_id ?? null,
        m_organization_id: contentToUse.m_organization_id ?? null,
        order: null,
        sort: null,
        page: pageToUse + 1,
        limit: rowsPerPage,
      });
      if (response.status !== "ok") {
        return;
      }
      const data = response.send_mails;
      const total = response.total_count;
      const status = response;
      console.log("メール送信履歴取得", response);
      setMails(data);
      setTotalData(total);
      // 検索ボタンで1ページ目を指定した場合はページ状態を同期し、確定条件を更新
      if (overridePage !== undefined) {
        setPage(overridePage);
        lastSearchedContentRef.current = searchContents;
      } else {
        // 初回またはページネーション時は今回使用した条件を保持
        lastSearchedContentRef.current = contentToUse;
      }
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  //初回起動時の読み込み回避
  const isFirstRender = useRef(false);

  useEffect(() => {
    // このeffectは初回レンダー時のみ呼ばれるeffect
    isFirstRender.current = true;
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      // 初回レンダー判定
      isFirstRender.current = false; // もう初回レンダーじゃないよ代入
    } else {
      fetchMails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  const [selectedRow, setSelectedRow] = useState<SendMailItem | null>(null);
  //*ポップアップ設定 */
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleLocationCodeClick = (row: SendMailItem) => {
    setSelectedRow(row);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  //スナックバー管理
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  //ページネーション
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //連絡先（権限）カラム表示内容
  const Organizations = ({
    organizations,
    mailId,
  }: {
    organizations?: SendMailOrganization[];
    mailId?: number;
  }) => {
    return (
      <Box>
        {Array.isArray(organizations)
          ? organizations.map((organization, index) => {
            return (
              <Box
                key={
                  organization.m_role.id === 0
                    ? `${mailId}-${organization.m_organization.id}-organization-${index}`
                    : `${mailId}-${organization.m_role.id}-role-${index}`
                }
                sx={{ color: "#0CA3B9", fontWeight: "700" }}
              >
                {organization.m_role.id === 0
                  ? organization.m_organization.department_name
                  : organization.m_role.name}
              </Box>
            );
          })
          : null}
      </Box>
    );
  };

  const ButtonVaridate = ({
    input,
    id,
  }: {
    input: number;
    id: number;
  }) => {
    // 送信ステータス(0:未送信, 1:送信中, 2:送信完了, 9:送信エラー)
    // return input !== 2 ? (
    return input === 2 ? (
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          width: "100%",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          sx={{ pointerEvents: "auto" }}
        // onClick={() => registerButtonClick(rowNum)}
        >
          確認
        </Button>
      </Box>
    ) : (
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          width: "100%",
          justifyContent: "flex-end",
        }}
      >
        <Button
          name="editButton"
          variant="outlined"
          sx={{ pointerEvents: "auto" }}
          // onClick={(e) => e.stopPropagation()}
          onClick={(e) => handleNavigate(e, `/mail/log/mailing?id=${id}`)}
        // onClick={() => handleLocationCodeClick(rowNum)}
        >
          編集
        </Button>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          gap: "16px",
          padding: "16px 0 32px",
          "@media screen and (max-width:900px)": {
            padding: "16px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            "@media screen and (max-width:600px)": {
              flexDirection: "column",
              alignItems: "center",
            },
          }}
        >
          <Button
            name="searchButton"
            variant="contained"
            onClick={() => {
            setPage(0);
            fetchMails(0);
          }}
            sx={{ width: "208px" }}
          >
            検索
          </Button>
          <Button
            name="createMailButton"
            variant="outlined"
            onClick={(e) => handleNavigate(e, "/mail/log/mailing")}
            sx={{ display: "flex", width: "208px", gap: "8px" }}
          >
            <EmailOutlinedIcon />
            メール作成
          </Button>
        </Box>
      </Box>
      <Divider />
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            gap: "16px",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            paddingBottom: "32px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              paddingLeft: "40px",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:900px)": {
                padding: "0",
              },
            }}
          >
            検索結果
          </Box>
        </Box>
        <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      sx={{
                        padding: "8px",
                        fontWeight: "bold",
                        borderBottom: 2,
                        borderBottomColor: "outline.dark",
                        "@media screen and (max-width:900px)": {
                          display:
                            column.isSmallScreen === true
                              ? "table-cell"
                              : "none",
                        },
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {mails?.map((mail, rowNum) => {
                  return (
                    <TableRow
                      {...({
                        name: `searchResult_${rowNum}`,
                      } as ComponentProps<typeof TableRow>)}
                      role="checkbox"
                      tabIndex={-1}
                      key={mail.id}
                      onClick={() => handleLocationCodeClick(mail)}
                      sx={{
                        pointerEvents: "none",
                        "@media screen and (max-width:900px)": {
                          cursor: "pointer",
                          pointerEvents: "auto",
                        },
                      }}
                    >
                      {columns.map((column) => {
                        return (
                          <TableCell
                            key={`${column.label}_${column.id}`}
                            align={column.align}
                            sx={{
                              padding: "8px",
                              "@media screen and (max-width:900px)": {
                                display:
                                  column.isSmallScreen === true
                                    ? "table-cell"
                                    : "none",
                              },
                            }}
                          >
                            {column.button ? (
                              <ButtonVaridate
                                input={mail.sent_status}
                                id={mail.id}
                              />
                            ) : column.organizations ? (
                              <Organizations
                                organizations={mail?.send_mail_organizations}
                                mailId={mail.id}
                              />
                            ) : column.disaster ? (
                              <>{mail?.disaster?.name}</>
                            ) : column.id === "sent_at" ||
                              column.id === "plan_at" ? (
                              formatDate(mail[column.id])
                            ) : column.id === "title" ||
                              column.id === "disaster_id" ? (
                              <>{mail[column.id]}</>
                            ) : null}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <SnackbarCustom
            data={snackBarstatus}
            openStatus={snackbarOpen}
            onClose={() => setSnackbarOpen(false)}
          />
          <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
            <Box
              sx={{
                display: "flex",
                height: "56px",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                borderBottom: "2px solid #CCC",
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  paddingLeft: "8px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                メール送信履歴詳細
              </Box>
              <IconButton
                aria-label="close"
                onClick={handlePopoverClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
              <TableBody>
                {selectedRow &&
                  columns.map((column) => {
                    return (
                      <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                        <TableCell
                          align={column.align}
                          sx={{
                            color: "outline.dark",
                            fontWeight: "bold",
                            boxSizing: "border-box",
                            width: "calc((100vw - 64px) / 2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              WebkitWritingMode: "horizontal-tb",
                              height: "100%",
                            }}
                          >
                            {column.label}
                          </Box>
                        </TableCell>
                        <TableCell
                          align={column.align}
                          sx={{
                            writingMode: "horizontal-tb",
                            boxSizing: "border-box",
                            width: "calc((100vw - 64px) / 2)",
                            overflowWrap: "break-word",
                          }}
                        >
                          {column.button ? (
                            <ButtonVaridate
                              input={selectedRow.sent_status}
                              id={selectedRow.id}
                            />
                          ) : column.organizations ? (
                            <Organizations
                              organizations={
                                selectedRow.send_mail_organizations
                              }
                            />
                          ) : column.id === "sent_at" ||
                            column.id === "plan_at" ? (
                            formatDate(selectedRow[column.id])
                          ) : column.id === "title" ||
                            column.id === "disaster_id" ? (
                            <>{selectedRow[column.id]}</>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Dialog>
          <TablePagination
            rowsPerPageOptions={[10, 30, 50, 100]}
            component="div"
            count={totalData ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
              {
                margin: "0 !important",
                padding: "0 !important",
              },
            }}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
          />
        </Paper>
      </Box>
    </>
  );
};
export default SearchResult;
